import { Popover } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { CirclePicker } from "react-color";
import { AI_MAPPING_CATEGORIES, getCategoryLabel } from "../aiMappingColors";
import { AiMappingDetectionLike, AiMappingSegmentationLike } from "../aiMappingTypes";
import { AiMappingController } from "../useAiMappingController";

interface AiMappingResultsPopoversProps {
  controller: AiMappingController;
  areaId: string;
  checkedSegmentations: AiMappingSegmentationLike[];
  activeSegmentations: AiMappingSegmentationLike[];
  bulkColorAnchor: HTMLElement | null;
  bulkColor: string;
  onBulkColorChange: (color: string) => void;
  onCloseBulkColor: () => void;
  editTarget: { anchor: HTMLElement; detection: AiMappingDetectionLike } | null;
  editCategory: string;
  editColor: string;
  onEditCategoryChange: (category: string) => void;
  onEditColorChange: (color: string) => void;
  onCloseEdit: () => void;
  onSaveEdit: () => void;
  segEditTarget: { anchor: HTMLElement; segmentation: AiMappingSegmentationLike } | null;
  segEditCategory: string;
  segEditColor: string;
  onSegEditCategoryChange: (category: string) => void;
  onSegEditColorChange: (color: string) => void;
  onCloseSegEdit: () => void;
  onSaveSegEdit: () => void;
}

const popoverPaperSx = {
  backgroundColor: "#ffffff",
  border: "1px solid",
  borderColor: "grey.200",
  borderRadius: "8px",
};

export default function AiMappingResultsPopovers({
  controller,
  areaId,
  checkedSegmentations,
  activeSegmentations,
  bulkColorAnchor,
  bulkColor,
  onBulkColorChange,
  onCloseBulkColor,
  editTarget,
  editCategory,
  editColor,
  onEditCategoryChange,
  onEditColorChange,
  onCloseEdit,
  onSaveEdit,
  segEditTarget,
  segEditCategory,
  segEditColor,
  onSegEditCategoryChange,
  onSegEditColorChange,
  onCloseSegEdit,
  onSaveSegEdit,
}: AiMappingResultsPopoversProps) {
  return (
    <>
      <Popover
        open={!!bulkColorAnchor}
        anchorEl={bulkColorAnchor}
        onClose={onCloseBulkColor}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: popoverPaperSx }}
      >
        <MDBox p={1.5} width={260} display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="button">
            {checkedSegmentations.length ? "Recolor selected areas" : "Recolor Run Area"}
          </MDTypography>
          <CirclePicker
            color={bulkColor}
            onChangeComplete={(color) => onBulkColorChange(color.hex)}
            circleSize={18}
            circleSpacing={4}
            width="100%"
          />
          <MDBox display="flex" gap={1}>
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              fullWidth
              onClick={onCloseBulkColor}
            >
              Cancel
            </MDButton>
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              fullWidth
              onClick={async () => {
                const targets = checkedSegmentations.length
                  ? [...checkedSegmentations]
                  : [...activeSegmentations];
                onCloseBulkColor();
                for (const segmentation of targets) {
                  await controller.updateSegmentation(areaId, segmentation.id, {
                    color: bulkColor,
                  });
                }
              }}
              data-testid="ai-mapping-apply-recolor"
            >
              Apply
            </MDButton>
          </MDBox>
        </MDBox>
      </Popover>

      <Popover
        open={!!editTarget}
        anchorEl={editTarget?.anchor ?? null}
        onClose={onCloseEdit}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: popoverPaperSx }}
      >
        <MDBox p={1.5} width={260} display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="button">Edit detection</MDTypography>
          <MDBox
            component="select"
            value={editCategory}
            onChange={(event: any) => onEditCategoryChange(event.target.value)}
            data-testid="ai-mapping-edit-category"
            sx={{
              height: 30,
              fontSize: 12,
              border: "1px solid #ddd",
              borderRadius: "6px",
              px: 1,
              bgcolor: "#fff",
            }}
          >
            {AI_MAPPING_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {getCategoryLabel(category)}
              </option>
            ))}
          </MDBox>
          <CirclePicker
            color={editColor}
            onChangeComplete={(color) => onEditColorChange(color.hex)}
            circleSize={18}
            circleSpacing={4}
            width="100%"
          />
          <MDBox display="flex" gap={1}>
            <MDButton size="small" variant="outlined" color="dark" fullWidth onClick={onCloseEdit}>
              Cancel
            </MDButton>
            <MDButton size="small" variant="gradient" color="info" fullWidth onClick={onSaveEdit}>
              Save
            </MDButton>
          </MDBox>
        </MDBox>
      </Popover>

      <Popover
        open={!!segEditTarget}
        anchorEl={segEditTarget?.anchor ?? null}
        onClose={onCloseSegEdit}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: popoverPaperSx }}
      >
        <MDBox p={1.5} width={260} display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="button">Edit area</MDTypography>
          <MDBox
            component="select"
            value={segEditCategory}
            onChange={(event: any) => onSegEditCategoryChange(event.target.value)}
            data-testid="ai-mapping-edit-segmentation-category"
            sx={{
              height: 30,
              fontSize: 12,
              border: "1px solid #ddd",
              borderRadius: "6px",
              px: 1,
              bgcolor: "#fff",
            }}
          >
            {AI_MAPPING_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {getCategoryLabel(category)}
              </option>
            ))}
          </MDBox>
          <CirclePicker
            color={segEditColor}
            onChangeComplete={(color) => onSegEditColorChange(color.hex)}
            circleSize={18}
            circleSpacing={4}
            width="100%"
          />
          <MDBox display="flex" gap={1}>
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              fullWidth
              onClick={onCloseSegEdit}
            >
              Cancel
            </MDButton>
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              fullWidth
              onClick={onSaveSegEdit}
              data-testid="ai-mapping-save-segmentation"
            >
              Save
            </MDButton>
          </MDBox>
        </MDBox>
      </Popover>
    </>
  );
}
